
import { defineComponent, computed, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';

import WdwDynbox from '@/components/general/WdwDynbox.vue';
import FormControlInput from '@/components/general/FormControl/Input.vue';
import FormControlSelect from '@/components/general/FormControl/Select.vue';
import FormControlSearchAttribute from '@/components/general/FormControl/SearchAttributes.vue';

import { Form } from 'vee-validate';

import { domainStore } from '@/store/domain-store';
import { askusStore } from '@/store/askus-store';

import useHashParams from '@/composables/general/useHashParams';
import useConverter from '@/composables/general/useConverter';
import { hash } from 'ol/tilecoord';

export default defineComponent({
  name: 'AskusFilter',
  props: {},
  components: {
    WdwDynbox,
    FormControlSelect,
    FormControlInput,
    FormControlSearchAttribute,
    Form,
  },
  setup(props, context) {
    const route = useRoute();
    const { setHashParams, deleteHashParams, getHashParams } = useHashParams(route);
    const { toBool } = useConverter();
    const sort = ref();
    const reloadList = ref(0);
    const wdwDynboxRef = ref();
    const componentKey = ref(0);
    const sortOptions = [
      domainStore.translate('askus.filter.sort-options.title-asc'),
      domainStore.translate('askus.filter.sort-options.title-desc'),
      domainStore.translate('askus.filter.sort-options.random'),
      domainStore.translate('askus.filter.sort-options.age-asc'),
      domainStore.translate('askus.filter.sort-options.age-desc'),
      domainStore.translate('askus.filter.sort-options.state'),
    ];

    const optionsMap = computed(() => {
      const map = new Map<string, string>();
      map.set(domainStore.translate('askus.filter.sort-options.title-asc'), '%2Btitle');
      map.set(domainStore.translate('askus.filter.sort-options.title-desc'), '-title');
      map.set(domainStore.translate('askus.filter.sort-options.random'), 'random');
      map.set(domainStore.translate('askus.filter.sort-options.age-asc'), '%2Bcreated');
      map.set(domainStore.translate('askus.filter.sort-options.age-desc'), '-created');
      map.set(domainStore.translate('askus.filter.sort-options.state'), '%2Bstate');
      return map;
    });

    const askus = computed(() => {
      return askusStore.getState().askus;
    });

    const askusDefaultFilter = computed(() => {
      return askusStore.getState().filter;
    });

    const askusAttrFilterValues = computed(() => {
      return askusStore.getState().filter.attributes;
    });

    const filterAttributes = computed(() => {
      const attribute = askusStore
        .getState()
        .askus.attributes.filter((x: any) => x.relation_data && toBool(x.relation_data.show_in_filter));
      return attribute.map((x: any) => {
        return {
          // filter_name is required to override default values for attributes
          filter_name: 'attribute' + x.id,
          ...x,
        };
      });
    });

    function getSortKey() {
      const defaulsort = askus.value.defaultSort;
      let tagetkey = '';
      optionsMap.value.forEach((val, key) => {
        if (val === defaulsort) {
          tagetkey = key;
        }
      });
      sort.value = optionsMap.value.get(tagetkey);
      return tagetkey;
    }

    function selectedElem(event: any, field: any) {
      sort.value = optionsMap.value.get(event.value);
    }

    function useFilter(values: any) {
      if (!Array.isArray(values.sort)) {
        sort.value = optionsMap.value.get(values.sort);
      }
      let textInput = '';
      if (values.textFilter) {
        textInput = values.textFilter;
      }

      // T23152: attribute values
      const attrValues: any = askusStore.filterHashForAttributeValues(values);

      askusStore.setFilter(sort.value, textInput, attrValues);
      askusStore.fetchQuestions();
      setHashParams(
        {
          ...attrValues,
          textFilter: values.textFilter,
          sort: sort.value,
        },
        false
      );
    }

    function resetFilter() {
      deleteHashParams(['textFilter']);
      setHashParams({ sort: askus.value.defaultSort });
      askusStore.setFilter(askus.value.defaultSort, '', {});
      askusStore.resetQuestion();
      reloadList.value += 1;
      // toggle Minimize (collapse filter again)
      wdwDynboxRef.value.toggleMinimize();
      // use custom key to force rerender (so as to reload form with default values)
      componentKey.value += 1;
    }

    return {
      domainStore,
      askusDefaultFilter,
      sortOptions,
      filterAttributes,
      askusAttrFilterValues,
      reloadList,
      componentKey,
      wdwDynboxRef,
      selectedElem,
      useFilter,
      resetFilter,
      getSortKey,
    };
  },
});
